import type { FC } from "react";

import type { DashboardOrganizationInvite } from "../../dashboard-client";

import { formatDateTime } from "../../utils/time";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { PanelSection } from "../common/PanelSection";

export type PendingInviteAction = "invalidate" | "resend";

interface PendingInviteActionState {
  inviteId: string;
  action: PendingInviteAction;
}

interface OrganizationPendingInvitesSectionProps {
  invites: DashboardOrganizationInvite[];
  actionState: PendingInviteActionState | null;
  onResend: (inviteId: string, email: string, role: string) => void;
  onInvalidate: (inviteId: string) => void;
}

function formatInviteDate(value: string): string {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return "\u2014";
  }
  return formatDateTime(timestamp);
}

export const OrganizationPendingInvitesSection: FC<
  OrganizationPendingInvitesSectionProps
> = ({ invites, actionState, onResend, onInvalidate }) => {
  if (invites.length === 0) {
    return (
      <PanelSection title="Pending Invites">
        <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
          No pending invites.
        </p>
      </PanelSection>
    );
  }

  return (
    <PanelSection title="Pending Invites">
      <DataTable responsive>
        <DataTable.Header>
          <tr>
            <DataTable.HeaderCell>Email</DataTable.HeaderCell>
            <DataTable.HeaderCell>Role</DataTable.HeaderCell>
            <DataTable.HeaderCell>Invited</DataTable.HeaderCell>
            <DataTable.HeaderCell>Expires</DataTable.HeaderCell>
            <DataTable.HeaderCell>Actions</DataTable.HeaderCell>
          </tr>
        </DataTable.Header>
        <DataTable.Body>
          {invites.map((invite) => {
            const currentAction =
              actionState?.inviteId === invite.id ? actionState.action : null;
            const isProcessing = currentAction !== null;
            return (
              <PendingInviteRow
                key={invite.id}
                invite={invite}
                currentAction={currentAction}
                isProcessing={isProcessing}
                onResend={onResend}
                onInvalidate={onInvalidate}
              />
            );
          })}
        </DataTable.Body>
      </DataTable>
    </PanelSection>
  );
};

interface PendingInviteRowProps {
  invite: DashboardOrganizationInvite;
  currentAction: PendingInviteAction | null;
  isProcessing: boolean;
  onResend: OrganizationPendingInvitesSectionProps["onResend"];
  onInvalidate: OrganizationPendingInvitesSectionProps["onInvalidate"];
}

function PendingInviteRow({
  invite,
  currentAction,
  isProcessing,
  onResend,
  onInvalidate,
}: PendingInviteRowProps) {
  return (
    <DataTable.Row key={invite.id}>
      <DataTable.Cell className="text-gray-900 dark:text-white">
        {invite.email}
      </DataTable.Cell>
      <DataTable.Cell className="capitalize text-gray-700 dark:text-gray-300">
        {invite.role}
      </DataTable.Cell>
      <DataTable.Cell className="text-gray-700 dark:text-gray-300">
        {formatInviteDate(invite.created_at)}
      </DataTable.Cell>
      <DataTable.Cell className="text-gray-700 dark:text-gray-300">
        {formatInviteDate(invite.expires_at)}
      </DataTable.Cell>
      <DataTable.Cell>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onResend(invite.id, invite.email, invite.role)}
            loading={currentAction === "resend"}
            loadingText="Resending..."
            disabled={isProcessing}
          >
            Resend
          </Button>
          <Button
            size="sm"
            variant="cancel"
            onClick={() => onInvalidate(invite.id)}
            loading={currentAction === "invalidate"}
            loadingText="Invalidating..."
            disabled={isProcessing}
          >
            Invalidate
          </Button>
        </div>
      </DataTable.Cell>
    </DataTable.Row>
  );
}
